import { apiFetch } from './client';

export const SLOT_CONFIG_ERRORS = {
  amounts_must_be_non_negative: 'All values must be zero or greater.',
  invalid_amounts: 'Enter valid numbers for the slot settings.',
};

export function normalizeSlotConfig(raw) {
  const config = raw?.config || raw || {};
  return {
    slotHorizonDays: config.slot_horizon_days ?? 3,
    cancelCutoffMinutes: config.cancel_cutoff_minutes ?? 30,
    maxOrdersPerSlot: config.max_orders_per_slot ?? 10,
    instantEtaMinutes: config.instant_eta_minutes ?? 45,
  };
}

function toPayload(data) {
  return {
    slot_horizon_days: Number(data.slotHorizonDays),
    cancel_cutoff_minutes: Number(data.cancelCutoffMinutes),
    max_orders_per_slot: Number(data.maxOrdersPerSlot),
    instant_eta_minutes: Number(data.instantEtaMinutes),
  };
}

export async function getSlotConfig(token) {
  const raw = await apiFetch('/config/delivery', { token });
  return normalizeSlotConfig(raw);
}

export async function updateSlotConfig(data, token) {
  const raw = await apiFetch('/config/delivery', {
    method: 'PUT',
    token,
    body: toPayload(data),
  });
  return normalizeSlotConfig(raw);
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const WEEKLY_SLOTS_ERRORS = {
  weekly_must_have_7_days: 'Slot schedule must include all 7 days.',
  slots_must_be_list: 'Something went wrong reading the slot list — try refreshing.',
  overnight_not_allowed: "A slot's end time must be after its start time on the same day.",
  slot_outside_store_hours: "That slot falls outside the day's store hours.",
  duplicate_template: 'Two slots have the same start and end time.',
  overlapping_slots: 'Slots on the same day cannot overlap.',
  invalid_max_orders: 'Max orders must be a whole number of at least 1.',
  forbidden: 'You do not have permission to do this.',
};

export function friendlyWeeklySlotsError(message) {
  if (!message) return null;
  const missingDay = /^missing_day_(\w+)$/.exec(message);
  if (missingDay) {
    const label = DAY_LABELS.find((d) => d.toLowerCase() === missingDay[1]) || missingDay[1];
    return `Missing ${label} in the slot schedule — try refreshing.`;
  }
  return WEEKLY_SLOTS_ERRORS[message] || message;
}

/** One local, React-key-stable id per slot — independent of the backend's derived template_id. */
function makeSlotKey() {
  return `k${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
}

export function normalizeWeeklySlots(raw) {
  const list = raw?.weekly_slots || [];
  const byDay = {};
  list.forEach((entry) => {
    const label = DAY_LABELS.find((d) => d.toLowerCase() === entry.day);
    if (!label) return;
    byDay[label] = (entry.slots || []).map((s) => ({
      _key: s.template_id || makeSlotKey(),
      templateId: s.template_id || null,
      start: s.start,
      end: s.end,
      label: s.label || '',
      enabled: s.enabled !== false,
      maxOrders: s.max_orders ?? null,
    }));
  });
  DAY_LABELS.forEach((label) => { if (!byDay[label]) byDay[label] = []; });
  return byDay;
}

function toWeeklySlotsPayload(byDay) {
  return DAY_LABELS.map((label) => ({
    day: label.toLowerCase(),
    slots: (byDay[label] || []).map((s) => ({
      start: s.start,
      end: s.end,
      ...(s.label ? { label: s.label } : {}),
      enabled: s.enabled !== false,
      ...(s.maxOrders !== null && s.maxOrders !== undefined && s.maxOrders !== ''
        ? { max_orders: Number(s.maxOrders) }
        : {}),
    })),
  }));
}

export async function getWeeklySlots(token) {
  const raw = await apiFetch('/config/delivery/slots', { token });
  return normalizeWeeklySlots(raw);
}

export async function updateWeeklySlots(byDay, token) {
  const raw = await apiFetch('/config/delivery/slots', {
    method: 'PUT',
    token,
    body: { weekly_slots: toWeeklySlotsPayload(byDay) },
  });
  return normalizeWeeklySlots(raw);
}
